import React, { useState, useEffect } from 'react'
import {
  getProfissionaisAdmin,
  createProfissional,
  updateProfissional,
  getServicosAdmin,
  getProfissionalServicosMap,
  setServicosDoProfissional,
  criarUtilizador,
  updatePerfil,
  reporPassword,
  getConfiguracoes,
  salvarOrdemProfissionais,
  uploadFotoProfissional,
} from '../../lib/supabase'
import { ordenarProfissionais } from '../../lib/profissionais'
import { useTenant } from '../../contexts/TenantContext'
import { useToast } from '../../contexts/ToastContext'
import { useAuth } from '../../contexts/AuthContext'
import Loading from '../../components/Loading'
import CropadorFoto from '../../components/CropadorFoto'

const PASSWORD_INICIAL = 'groupcentra'

// Papéis no backoffice que um profissional pode ter (além de estar na agenda).
// Definir Manager/Super Admin só está disponível para superuser (ver RLS perfis_all_superuser).
const ROLE_LABELS = { funcionario: 'Funcionário', manager: 'Manager', superuser: 'Super Admin' }

const inputStyle = {
  width: '100%', padding: '9px 12px',
  border: '1px solid var(--cor-borda)', borderRadius: 'var(--raio)',
  fontSize: '14px', fontFamily: 'var(--font-ui)',
  background: 'var(--cor-branco)', color: 'var(--cor-texto)',
}

const VAZIO = { nome: '', email: '', telefone: '', cor_agenda: '#8B4A2F', ativo: true, servicos: [], role: 'funcionario', foto_url: '' }

function SeletorServicos({ servicos, selecionados, onToggle }) {
  const ativos = servicos.filter(s => s.ativo)
  const categorias = [...new Set(ativos.map(s => s.categoria))]
  const todos = ativos.map(s => s.id)
  const todosSelecionados = todos.length > 0 && todos.every(id => selecionados.includes(id))

  function alternarTodos() {
    if (todosSelecionados) {
      todos.forEach(id => selecionados.includes(id) && onToggle(id))
    } else {
      todos.forEach(id => !selecionados.includes(id) && onToggle(id))
    }
  }

  if (ativos.length === 0) {
    return (
      <p style={{ fontSize: '13px', color: 'var(--cor-texto-suave)' }}>
        Sem serviços ativos. Adicione serviços na secção “Serviços” primeiro.
      </p>
    )
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
        <span style={{ fontSize: '12px', color: 'var(--cor-texto-suave)' }}>
          {selecionados.length} de {ativos.length} selecionados
        </span>
        <button
          type="button"
          onClick={alternarTodos}
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            fontSize: '12px', color: 'var(--cor-principal)', textDecoration: 'underline',
            padding: 0,
          }}
        >
          {todosSelecionados ? 'Limpar tudo' : 'Selecionar tudo'}
        </button>
      </div>

      <div style={{
        maxHeight: '320px', overflowY: 'auto',
        border: '1px solid var(--cor-borda)', borderRadius: 'var(--raio)',
        background: 'var(--cor-branco)', padding: '8px',
      }}>
        {categorias.map(cat => (
          <div key={cat} style={{ marginBottom: '10px' }}>
            <p style={{
              fontSize: '10px', fontWeight: '600',
              textTransform: 'uppercase', letterSpacing: '0.08em',
              color: 'var(--cor-texto-suave)',
              padding: '6px 8px 4px',
            }}>
              {cat}
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
              {ativos.filter(s => s.categoria === cat).map(s => {
                const checked = selecionados.includes(s.id)
                return (
                  <label
                    key={s.id}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '10px',
                      padding: '7px 8px', cursor: 'pointer',
                      borderRadius: 'var(--raio)',
                      background: checked ? 'var(--cor-creme)' : 'transparent',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => onToggle(s.id)}
                      style={{ width: '15px', height: '15px', accentColor: 'var(--cor-principal)', flexShrink: 0 }}
                    />
                    <span style={{ flex: 1, fontSize: '13px' }}>{s.nome}</span>
                    <span style={{ fontSize: '11px', color: 'var(--cor-texto-suave)', whiteSpace: 'nowrap' }}>
                      {s.duracao_min} min
                    </span>
                  </label>
                )
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

function FormProfissional({ form, setForm, servicos, onSave, onCancel, guardando, titulo, podeDefinirRole, onSelecionarFoto, aEnviarFoto }) {
  function toggleServico(id) {
    setForm(p => ({
      ...p,
      servicos: p.servicos.includes(id)
        ? p.servicos.filter(x => x !== id)
        : [...p.servicos, id],
    }))
  }

  return (
    <div style={{
      background: 'var(--cor-creme)', border: '1px solid var(--cor-borda)',
      borderRadius: 'var(--raio-lg)', padding: '20px',
    }}>
      <p style={{ fontWeight: '600', color: 'var(--cor-principal)', marginBottom: '16px' }}>{titulo}</p>
      <form onSubmit={onSave}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '16px' }}>
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontSize: '13px', fontWeight: '500' }}>Nome *</label>
            <input
              type="text"
              value={form.nome}
              onChange={e => setForm(p => ({ ...p, nome: e.target.value }))}
              style={{ ...inputStyle, background: 'var(--cor-branco)' }}
              required
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontSize: '13px', fontWeight: '500' }}>Email *</label>
            <input
              type="email"
              value={form.email}
              onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
              style={{ ...inputStyle, background: 'var(--cor-branco)' }}
              required
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontSize: '13px', fontWeight: '500' }}>Telefone</label>
            <input
              type="tel"
              value={form.telefone}
              onChange={e => setForm(p => ({ ...p, telefone: e.target.value }))}
              style={{ ...inputStyle, background: 'var(--cor-branco)' }}
            />
          </div>
          {podeDefinirRole && (
            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontSize: '13px', fontWeight: '500' }}>Papel no backoffice</label>
              <select
                value={form.role}
                onChange={e => setForm(p => ({ ...p, role: e.target.value }))}
                style={{ ...inputStyle, background: 'var(--cor-branco)' }}
              >
                <option value="funcionario">{ROLE_LABELS.funcionario}</option>
                <option value="manager">{ROLE_LABELS.manager}</option>
                <option value="superuser">{ROLE_LABELS.superuser}</option>
              </select>
            </div>
          )}
          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontSize: '13px', fontWeight: '500' }}>Cor na agenda</label>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              <input
                type="color"
                value={form.cor_agenda}
                onChange={e => setForm(p => ({ ...p, cor_agenda: e.target.value }))}
                style={{ width: '46px', height: '38px', border: '1px solid var(--cor-borda)', borderRadius: 'var(--raio)', padding: '2px', cursor: 'pointer' }}
              />
              <span style={{ fontSize: '13px', color: 'var(--cor-texto-suave)', fontFamily: 'monospace' }}>{form.cor_agenda}</span>
            </div>
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontSize: '13px', fontWeight: '500' }}>Fotografia</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap' }}>
              {form.foto_url ? (
                <img src={form.foto_url} alt="" style={{ width: '64px', height: '64px', borderRadius: '50%', objectFit: 'cover', border: '1px solid var(--cor-borda)', flexShrink: 0 }} />
              ) : (
                <div style={{ width: '64px', height: '64px', borderRadius: '50%', background: 'var(--cor-creme-escuro)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', color: 'var(--cor-texto-suave)', textAlign: 'center', flexShrink: 0 }}>
                  sem foto
                </div>
              )}
              <div>
                <input
                  type="file"
                  accept="image/*"
                  onChange={e => { const f = e.target.files?.[0]; if (f) onSelecionarFoto(f); e.target.value = '' }}
                  disabled={aEnviarFoto}
                  style={{ fontSize: '13px', display: 'block', marginBottom: '6px' }}
                />
                {aEnviarFoto && <p style={{ fontSize: '12px', color: 'var(--cor-texto-suave)', margin: 0 }}>A carregar…</p>}
                {form.foto_url && !aEnviarFoto && (
                  <button type="button" onClick={() => setForm(p => ({ ...p, foto_url: '' }))} style={{ background: 'none', border: 'none', color: '#DC2626', cursor: 'pointer', fontSize: '12px', padding: 0 }}>
                    Remover foto
                  </button>
                )}
              </div>
            </div>
            <p style={{ fontSize: '12px', color: 'var(--cor-texto-suave)', marginTop: '6px', lineHeight: 1.5 }}>
              Mostrada aos clientes ao escolher a profissional. Depois de carregar a imagem pode arrastar e ajustar o zoom para escolher a parte a usar.
            </p>
          </div>
        </div>

        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: '500' }}>
            Serviços que realiza
          </label>
          <SeletorServicos
            servicos={servicos}
            selecionados={form.servicos}
            onToggle={toggleServico}
          />
        </div>

        <div style={{ display: 'flex', gap: '10px' }}>
          <button type="submit" className="btn-primario" disabled={guardando} style={{ opacity: guardando ? 0.6 : 1, padding: '9px 22px', fontSize: '14px' }}>
            {guardando ? 'A guardar…' : 'Guardar'}
          </button>
          <button type="button" onClick={onCancel} className="btn-secundario" style={{ padding: '9px 22px', fontSize: '14px' }}>
            Cancelar
          </button>
        </div>
      </form>
    </div>
  )
}

export default function Profissionais() {
  const { tenant } = useTenant()
  const toast = useToast()
  const { isSuper, isManager } = useAuth()
  const [lista, setLista] = useState([])
  const [servicos, setServicos] = useState([])
  const [servicosMap, setServicosMap] = useState({})
  const [carregando, setCarregando] = useState(true)
  const [editando, setEditando] = useState(null)
  const [form, setForm] = useState(VAZIO)
  const [guardando, setGuardando] = useState(false)
  const [ordemProf, setOrdemProf] = useState('')
  const [guardandoOrdem, setGuardandoOrdem] = useState(false)
  const [ficheiroCrop, setFicheiroCrop] = useState(null)
  const [aEnviarFoto, setAEnviarFoto] = useState(false)

  useEffect(() => {
    if (!tenant) return
    Promise.all([getProfissionaisAdmin(tenant.id), getServicosAdmin(tenant.id), getProfissionalServicosMap(tenant.id), getConfiguracoes(tenant.id)])
      .then(([profs, svcs, mapa, cfg]) => {
        setLista(profs)
        setServicos(svcs)
        setServicosMap(mapa)
        setOrdemProf(cfg.ordem_profissionais || '')
      })
      .catch(e => toast.erro(e.message))
      .finally(() => setCarregando(false))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenant])

  const listaOrdenada = ordenarProfissionais(lista, ordemProf)

  // Move um profissional uma posição para a esquerda/direita (Agenda do Dia)
  // e grava a nova ordem.
  async function moverProfissional(id, direcao) {
    const idx = listaOrdenada.findIndex(p => p.id === id)
    const alvo = idx + direcao
    if (alvo < 0 || alvo >= listaOrdenada.length) return
    const nova = listaOrdenada.map(p => p.id)
    ;[nova[idx], nova[alvo]] = [nova[alvo], nova[idx]]
    const anterior = ordemProf
    setOrdemProf(JSON.stringify(nova)) // otimista
    setGuardandoOrdem(true)
    try {
      await salvarOrdemProfissionais(tenant.id, nova)
    } catch (e) {
      setOrdemProf(anterior) // reverte
      toast.erro(e.message)
    } finally {
      setGuardandoOrdem(false)
    }
  }

  function iniciarEdicao(prof) {
    setEditando(prof.id)
    setForm({
      nome: prof.nome,
      email: prof.email || '',
      telefone: prof.telefone || '',
      cor_agenda: prof.cor_agenda || '#8B4A2F',
      ativo: prof.ativo,
      servicos: servicosMap[prof.id] || [],
      role: (prof.perfis || [])[0]?.role || 'funcionario',
      foto_url: prof.foto_url || '',
    })
    toast.erro(null)
  }

  function selecionarFoto(file) {
    setFicheiroCrop(file)
  }

  async function confirmarRecorte(blob) {
    setAEnviarFoto(true)
    try {
      const url = await uploadFotoProfissional(tenant.id, blob)
      setForm(p => ({ ...p, foto_url: url }))
    } catch (e) {
      toast.erro(e.message)
    } finally {
      setAEnviarFoto(false)
      setFicheiroCrop(null)
    }
  }

  function iniciarNovo() {
    setEditando('novo')
    setForm(VAZIO)
    toast.erro(null)
  }

  function cancelar() { setEditando(null); toast.erro(null) }

  async function guardar(e) {
    e.preventDefault()
    if (!form.nome.trim()) { toast.erro('O nome é obrigatório.'); return }
    if (editando === 'novo' && !form.email.trim()) { toast.erro('O email é obrigatório para criar o acesso ao backoffice.'); return }
    setGuardando(true)
    toast.erro(null)
    try {
      const dados = {
        nome: form.nome.trim(),
        email: form.email.trim() || null,
        telefone: form.telefone.trim() || null,
        cor_agenda: form.cor_agenda,
        ativo: form.ativo,
        foto_url: form.foto_url.trim() || null,
      }
      // Só o superuser pode atribuir papéis elevados (RLS perfis_all_superuser);
      // para os restantes o papel fica sempre 'funcionario'.
      const roleEscolhido = isSuper ? form.role : 'funcionario'
      let profId
      if (editando === 'novo') {
        const novo = await createProfissional(tenant.id, dados)
        profId = novo.id
        try {
          const perfilNovo = await criarUtilizador(tenant.id, {
            email: dados.email,
            password: PASSWORD_INICIAL,
            role: roleEscolhido,
            profissional_id: profId,
          })
          novo.perfis = [{ id: perfilNovo.id, email: perfilNovo.email, ativo: perfilNovo.ativo, role: perfilNovo.role }]
        } catch (err) {
          await updateProfissional(tenant.id, profId, { ativo: false })
          throw new Error(`Falhou ao criar o acesso: ${err.message} A profissional foi guardada como inativa — edita o email e clica em Ativar para tentar de novo.`)
        }
        setLista(prev => [...prev, novo].sort((a, b) => a.nome.localeCompare(b.nome)))
      } else {
        profId = editando
        await updateProfissional(tenant.id, editando, dados)
        // Atualizar o papel do perfil ligado se o superuser o alterou.
        const profAtual = lista.find(p => p.id === editando)
        const perfilLig = (profAtual?.perfis || [])[0]
        let perfisAtualizados = profAtual?.perfis
        if (isSuper && perfilLig && perfilLig.role !== roleEscolhido) {
          await updatePerfil(tenant.id, perfilLig.id, { role: roleEscolhido })
          perfisAtualizados = [{ ...perfilLig, role: roleEscolhido }]
        }
        setLista(prev => prev.map(p => p.id === editando ? { ...p, ...dados, perfis: perfisAtualizados } : p))
      }
      await setServicosDoProfissional(tenant.id, profId, form.servicos)
      setServicosMap(prev => ({ ...prev, [profId]: [...form.servicos] }))
      setEditando(null)
    } catch (e) {
      toast.erro(e.message)
    } finally {
      setGuardando(false)
    }
  }

  async function toggleAtivo(prof) {
    try {
      const novoAtivo = !prof.ativo
      // Se está a ativar e não tem perfil, tenta criar o acesso primeiro
      if (novoAtivo && !(prof.perfis || []).length) {
        if (!prof.email) {
          toast.erro('Não é possível ativar: a profissional não tem email para criar o acesso.')
          return
        }
        const perfilNovo = await criarUtilizador(tenant.id, {
          email: prof.email,
          password: PASSWORD_INICIAL,
          role: 'funcionario',
          profissional_id: prof.id,
        })
        await updateProfissional(tenant.id, prof.id, { ativo: true })
        setLista(prev => prev.map(p => p.id === prof.id
          ? { ...p, ativo: true, perfis: [{ id: perfilNovo.id, email: perfilNovo.email, ativo: perfilNovo.ativo, role: perfilNovo.role }] }
          : p))
        return
      }
      await updateProfissional(tenant.id, prof.id, { ativo: novoAtivo })
      setLista(prev => prev.map(p => p.id === prof.id ? { ...p, ativo: novoAtivo } : p))
    } catch (e) {
      toast.erro(e.message)
    }
  }

  async function toggleAcesso(prof) {
    const perfil = (prof.perfis || [])[0]
    if (!perfil) return
    try {
      await updatePerfil(tenant.id, perfil.id, { ativo: !perfil.ativo })
      setLista(prev => prev.map(p => p.id === prof.id
        ? { ...p, perfis: [{ ...perfil, ativo: !perfil.ativo }] }
        : p))
    } catch (e) {
      toast.erro(e.message)
    }
  }

  async function reporPasswordProf(prof) {
    const perfil = (prof.perfis || [])[0]
    if (!perfil) return
    if (!window.confirm(`Repor a palavra-passe de "${prof.nome}"? Fica com a inicial "${PASSWORD_INICIAL}" e terá de definir uma nova no próximo login.`)) return
    try {
      await reporPassword(tenant.id, perfil.id)
      toast.sucesso(`Palavra-passe reposta. ${prof.nome} terá de definir uma nova no próximo login.`)
    } catch (e) {
      toast.erro(e.message)
    }
  }

  return (
    <div style={{ maxWidth: '700px', margin: '0 auto', padding: '32px 24px 64px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
        <h2 style={{ fontSize: '22px' }}>Profissionais</h2>
        <button onClick={iniciarNovo} className="btn-primario" style={{ padding: '9px 20px', fontSize: '14px' }}>
          + Nova
        </button>
      </div>
      <p style={{ color: 'var(--cor-texto-suave)', marginBottom: '28px', fontSize: '14px' }}>
        Gestão das colaboradoras do centro e dos serviços que cada uma realiza. Usa as setas para definir a ordem em que aparecem, da esquerda para a direita, na Agenda do Dia.
      </p>

      {carregando ? <Loading /> : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {editando === 'novo' && (
            <FormProfissional
              form={form} setForm={setForm} servicos={servicos}
              onSave={guardar} onCancel={cancelar}
              guardando={guardando} titulo="Nova Profissional"
              podeDefinirRole={isSuper}
              onSelecionarFoto={selecionarFoto} aEnviarFoto={aEnviarFoto}
            />
          )}

          {listaOrdenada.map((prof, idx) => {
            const totalServicos = (servicosMap[prof.id] || []).length
            const perfil = (prof.perfis || [])[0]
            return (
              <div key={prof.id}>
                <div style={{
                  background: 'var(--cor-branco)',
                  border: `1px solid ${prof.ativo ? 'var(--cor-borda)' : '#E5E7EB'}`,
                  borderRadius: 'var(--raio-lg)', padding: '16px 20px',
                  display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap',
                  opacity: prof.ativo ? 1 : 0.55, boxShadow: 'var(--sombra)',
                }}>
                  {listaOrdenada.length > 1 && (
                    <span style={{ display: 'inline-flex', flexDirection: 'column', gap: '2px', flexShrink: 0 }}>
                      <button
                        type="button"
                        onClick={() => moverProfissional(prof.id, -1)}
                        disabled={idx === 0 || guardandoOrdem}
                        title="Mover para a esquerda na Agenda do Dia"
                        aria-label={`Mover ${prof.nome} para a esquerda`}
                        style={{ background: 'none', border: '1px solid var(--cor-borda)', borderRadius: 'var(--raio)', width: '22px', height: '22px', lineHeight: 1, cursor: idx === 0 ? 'default' : 'pointer', opacity: idx === 0 ? 0.35 : 1, fontSize: '12px', color: 'var(--cor-texto-suave)', padding: 0 }}
                      >
                        ↑
                      </button>
                      <button
                        type="button"
                        onClick={() => moverProfissional(prof.id, 1)}
                        disabled={idx === listaOrdenada.length - 1 || guardandoOrdem}
                        title="Mover para a direita na Agenda do Dia"
                        aria-label={`Mover ${prof.nome} para a direita`}
                        style={{ background: 'none', border: '1px solid var(--cor-borda)', borderRadius: 'var(--raio)', width: '22px', height: '22px', lineHeight: 1, cursor: idx === listaOrdenada.length - 1 ? 'default' : 'pointer', opacity: idx === listaOrdenada.length - 1 ? 0.35 : 1, fontSize: '12px', color: 'var(--cor-texto-suave)', padding: 0 }}
                      >
                        ↓
                      </button>
                    </span>
                  )}
                  {prof.foto_url ? (
                    <img src={prof.foto_url} alt="" style={{ width: '44px', height: '44px', borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
                  ) : (
                    <div style={{
                      width: '44px', height: '44px', borderRadius: '50%',
                      background: prof.cor_agenda, color: 'white',
                      fontSize: '18px', fontWeight: '700',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    }}>
                      {prof.nome.charAt(0)}
                    </div>
                  )}
                  <div style={{ flex: 1, minWidth: '160px' }}>
                    <div style={{ fontWeight: '600' }}>{prof.nome}</div>
                    {prof.email && <div style={{ fontSize: '13px', color: 'var(--cor-texto-suave)' }}>{prof.email}</div>}
                    <div style={{ fontSize: '12px', color: totalServicos === 0 ? '#DC2626' : 'var(--cor-texto-suave)', marginTop: '2px' }}>
                      {totalServicos === 0
                        ? 'Sem serviços atribuídos'
                        : `${totalServicos} serviço${totalServicos === 1 ? '' : 's'}`}
                    </div>
                    <div style={{ fontSize: '11px', marginTop: '4px', display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                      {!prof.ativo && <span style={{ color: '#9CA3AF' }}>Inativa</span>}
                      {perfil
                        ? perfil.ativo
                          ? <span style={{ color: '#16A34A' }}>✓ Tem acesso</span>
                          : <span style={{ color: '#DC2626' }}>✗ Acesso bloqueado</span>
                        : <span style={{ color: '#9CA3AF' }}>Sem acesso</span>}
                      {perfil && perfil.role && perfil.role !== 'funcionario' && (
                        <span style={{ color: '#2563EB', fontWeight: '600' }}>{ROLE_LABELS[perfil.role] ?? perfil.role}</span>
                      )}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                    {perfil && (
                      <button
                        onClick={() => toggleAcesso(prof)}
                        style={{ background: 'none', border: '1px solid var(--cor-borda)', borderRadius: 'var(--raio)', padding: '5px 12px', cursor: 'pointer', fontSize: '12px', color: 'var(--cor-texto-suave)' }}
                      >
                        {perfil.ativo ? 'Bloquear acesso' : 'Repor acesso'}
                      </button>
                    )}
                    {perfil && isManager && (
                      <button
                        onClick={() => reporPasswordProf(prof)}
                        style={{ background: 'none', border: '1px solid var(--cor-borda)', borderRadius: 'var(--raio)', padding: '5px 12px', cursor: 'pointer', fontSize: '12px', color: 'var(--cor-texto-suave)' }}
                      >
                        Repor palavra-passe
                      </button>
                    )}
                    <button
                      onClick={() => toggleAtivo(prof)}
                      style={{ background: 'none', border: '1px solid var(--cor-borda)', borderRadius: 'var(--raio)', padding: '5px 12px', cursor: 'pointer', fontSize: '12px', color: 'var(--cor-texto-suave)' }}
                    >
                      {prof.ativo ? 'Desativar' : 'Ativar'}
                    </button>
                    <button
                      onClick={() => editando === prof.id ? cancelar() : iniciarEdicao(prof)}
                      className="btn-secundario"
                      style={{ padding: '5px 12px', fontSize: '12px' }}
                    >
                      {editando === prof.id ? 'Cancelar' : 'Editar'}
                    </button>
                  </div>
                </div>
                {editando === prof.id && (
                  <div style={{ marginTop: '4px' }}>
                    <FormProfissional
                      form={form} setForm={setForm} servicos={servicos}
                      onSave={guardar} onCancel={cancelar}
                      guardando={guardando} titulo="Editar Profissional"
                      podeDefinirRole={isSuper}
                      onSelecionarFoto={selecionarFoto} aEnviarFoto={aEnviarFoto}
                    />
                  </div>
                )}
              </div>
            )
          })}

          {lista.length === 0 && editando !== 'novo' && (
            <p style={{ color: 'var(--cor-texto-suave)', textAlign: 'center', padding: '32px' }}>
              Sem profissionais registadas.
            </p>
          )}
        </div>
      )}

      {ficheiroCrop && (
        <CropadorFoto
          file={ficheiroCrop}
          onConfirmar={confirmarRecorte}
          onCancelar={() => setFicheiroCrop(null)}
        />
      )}
    </div>
  )
}
